import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { URLS } from '@/constants';
import { PiniaStoreId } from '@/constants/pinia';
import { UNIFIED_API_URL_KEY } from '@/constants/storage';
import { loadDatadogRum } from '@/datadog';
import { loadSentry } from '@/sentry';
import { AppConfigData } from '@/services/api/models/appConfig';
import apiSession from '@/services/session';
import getCookie from '@/utils/getCookie';
import setCookie from '@/utils/setCookie';

/**
 * Currently, the auth store is only used for the unified API. All other API calls and pages requiring authentication use the Django session cookie "mp-sessionid".
 */
export const useAppConfigStore = defineStore(PiniaStoreId.AppConfig, () => {
  const hasLoaded = ref(false);
  const hasSegmentLoaded = ref(false);
  const hasSentryLoaded = ref(false);
  const hasDatadogRumLoaded = ref(false);
  const hasDelightedLoaded = ref(false);

  const harmonyUpstreamConn = ref<AppConfigData['harmonyUpstreamConn']>();
  const environment = ref<AppConfigData['environment']>();
  const release = ref<AppConfigData['release']>();
  const buildInfo = ref<AppConfigData['buildInfo']>();
  const unifiedConfig = ref<AppConfigData['unifiedConfig']>();

  async function fetchAppConfig() {
    const response = await apiSession.get<AppConfigData>(URLS.APP_CONFIG);

    harmonyUpstreamConn.value = response.data.harmonyUpstreamConn;
    environment.value = response.data.environment;
    release.value = response.data.release;
    buildInfo.value = response.data.buildInfo;
    unifiedConfig.value = response.data.unifiedConfig;

    setUnifiedApiUrl({ url: unifiedConfig.value.url });

    hasLoaded.value = true;
  }

  /** The default location host for the marketplace API and server-rendered pages (excludes the URL protocol). */
  const defaultMpApiHost = computed<string>(() => {
    if (window.location.host.includes('localhost')) {
      return new URL(import.meta.env.VITE_MP_API_URL || window.location.href).host;
    }

    return window.location.host;
  });
  /** The active location host for the marketplace API and server-rendered pages (excludes the URL protocol). */
  const mpApiHost = ref<string>(getCookie('mp-api-host') || defaultMpApiHost.value);
  const isMpApiHostCustom = computed<boolean>(() => mpApiHost.value !== defaultMpApiHost.value);

  /**
   * Sets the active marketplace API host to the provided value. The provided host is also cached in a cookie.
   *
   * **Note:** the cookie is read by the reverse proxy (in CloudFront or locally) to determine the API host.
   */
  function setMpApiHost({ host, reloadWindow = false }: { host: string; reloadWindow?: boolean }) {
    mpApiHost.value = host.trim() || defaultMpApiHost.value;
    setCookie('mp-api-host', mpApiHost.value);

    if (reloadWindow) {
      window.location.reload();
    }
  }

  /** The default base URL for the unified API. */
  const defaultUnifiedApiUrl = computed<string>(() => unifiedConfig.value?.url || window.location.origin);
  /** The active base URL for the unified API. */
  const unifiedApiUrl = ref<string>(localStorage.getItem(UNIFIED_API_URL_KEY) || defaultUnifiedApiUrl.value);
  const isUnifiedApiUrlCustom = computed<boolean>(() => unifiedApiUrl.value !== defaultUnifiedApiUrl.value);

  /**
   * Sets the active base URL for the unified API to the provided value. The provided url is also cached in localStorage.
   */
  function setUnifiedApiUrl({ url, reloadWindow = false }: { url: string; reloadWindow?: boolean }) {
    unifiedApiUrl.value = url.trim() || defaultUnifiedApiUrl.value;
    localStorage.setItem(UNIFIED_API_URL_KEY, unifiedApiUrl.value);

    if (reloadWindow) {
      window.location.reload();
    }
  }

  watch(
    hasLoaded,
    () => {
      if (hasLoaded.value) {
        loadSentry();
        loadDatadogRum();
      }
    },
    { immediate: true },
  );

  return {
    fetchAppConfig,
    hasLoaded,
    hasSegmentLoaded,
    hasSentryLoaded,
    hasDatadogRumLoaded,
    hasDelightedLoaded,
    harmonyUpstreamConn,
    environment,
    release,
    buildInfo,
    unifiedConfig,
    /** The default location host for the marketplace API and server-rendered pages (excludes the URL protocol). */
    defaultMpApiHost,
    /** The active location host for the marketplace API and server-rendered pages (excludes the URL protocol). */
    mpApiHost,
    isMpApiHostCustom,
    setMpApiHost,
    /** The default base URL for the unified API. */
    defaultUnifiedApiUrl,
    /** The active base URL for the unified API. */
    unifiedApiUrl,
    isUnifiedApiUrlCustom,
    setUnifiedApiUrl,
  };
});
